/** Shared helpers for the map views: asset library access, cached materials, primitive builders, HQ-set mapping tables. */
import * as THREE from "three";
import { Cell, Grid, Heading } from "@highmarch/domain";
import { AssetLibrary } from "../assets";
import { tierFourName, towerModelName } from "../skins/kit/towerModels";

let library: AssetLibrary | null = null;
export function setAssetLibrary(lib: AssetLibrary): void { library = lib; }
export const lib = (): AssetLibrary | null => library && library.ready ? library : null;

const materials = new Map<number, THREE.Material>();
export function mat(color: number, transparent = false): THREE.Material {
  const key = color * 2 + (transparent ? 1 : 0);
  let m = materials.get(key);
  if (!m) {
    m = transparent ? new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.55, depthWrite: false }) : (lib()?.toonMaterial(color) ?? new THREE.MeshLambertMaterial({ color }));
    materials.set(key, m);
  }
  return m;
}
export const box = new THREE.BoxGeometry(1, 1, 1);
export const capsule = new THREE.CapsuleGeometry(0.22, 0.4, 4, 8);
export const quad = new THREE.PlaneGeometry(1, 1);

export function cube(pos: THREE.Vector3, sx: number, sy: number, sz: number, color: number): THREE.Mesh {
  const m = new THREE.Mesh(box, mat(color));
  m.position.copy(pos);
  m.scale.set(sx, sy, sz);
  m.castShadow = m.receiveShadow = true;
  return m;
}

/** Deterministic pseudo-random per cell for decor variation. */
export function cellHash(c: Cell): number { return ((c.x * 73856093) ^ (c.y * 19349663)) >>> 0; }

export const yawFor: Record<Heading, number> = { [Heading.North]: 0, [Heading.East]: -Math.PI / 2, [Heading.South]: Math.PI, [Heading.West]: Math.PI / 2 };
/**
 * Map heights without the HQ ground kit in the build (cell units): the surface at 0.2, the road bed at 0.1. They date
 * from the Kenney tile kit (removed 2026-10-14); only the no-HQ primitive fallback still uses them.
 */
export const SURFACE_Y = 0.2;
export const ROAD_Y = 0.1;

/**
 * HQ art set (ART_STATUS.md): model names per game id; each entry is used only when `lib().hq(name)` is true; a view
 * whose model is not in draws nothing for it. HQ models are 1×1 cells with the ground top at 0
 * and their front on +y in Blender, which the glTF exporter turns into -z: HQ_YAW turns that to face our +z (north).
 * Every HQ model is a rigid-part rig with baked clips (art/hq/README.md); `AnimRig` in ../rig.ts plays them.
 */
export const HQ_YAW = Math.PI;
/**
 * Tower archetype id → HQ model at a tier (`tower_<id>_t<1-3>`, `base_t<1-3>`). A fortress `skin` (views/baseSkin.ts)
 * picks `base_t<tier>__<skin>` when the manifest has that model, else the stock one. Tier 4 (M21) is the branch's own
 * model, `tower_<id>_t4_<branch>`, when the manifest has it, else the t3 model (towerModel.ts then dresses it in gold).
 * For a tower, `skin` is a tower kit's whole-model suffix (TowerKit.model, big kits: WornSkins.towerModel): its own
 * `tower_<id>_t<n>__<skin>` (or `…_t4_<branch>__<skin>`) when loaded, else as above (skins/kit/towerModels.ts).
 */
export function hqTowerName(archId: string, isBase: boolean, tier: number, skin: string | null = null, branch: string | null = null): string {
  if (!isBase) return towerModelName(archId, tier, branch, skin, (n) => lib()?.hq(n) ?? false);
  const t = Math.max(1, Math.min(3, tier));
  if (skin) { const skinned = `base_t${t}__${skin}`; if (lib()?.hq(skinned)) return skinned; }
  return "base_t" + t;
}
/** A tier-IV branch's model name (`<tower>_t4_<branch>` view key, the ascend-art agents' GLB). */
export function tierFourModel(archId: string, branch: string): string { return tierFourName(archId, branch); }
/** Enemy id → HQ model: one model per id, bosses by their own id; no per-id scale or tint (the models carry both). */
export function hqEnemyName(enemyId: string): string { return enemyId === "grunt" ? "enemy_raider" : enemyId.startsWith("boss_") ? enemyId : `enemy_${enemyId}`; }
/**
 * Per-archetype enemy scale on top of the shipped models (art review units F2). The AAA rebake normalised six of the
 * seven grunts to 0.33 tall, so a runner stood as tall as a shieldbearer; this restores the size spread the design
 * had (runner small and quick, brute a head taller) and makes bosses read as bosses. Bosses (rares/bosses review
 * 2026-10-27, "bosses must dominate the screen"): ×1.95, a body ~1.4-1.6 tall with its banner / stacks / staff rising to
 * 2.2-2.8 beside a 2.65 archer t3 (was ×1.45: 1.0-1.45, a t1 drum); the colossus, a basalt giant, ×2.8 (was ×2: the
 * shortest boss). Purely visual: the sim never reads it. Keyed by the balance id (`grunt` is the raider model); `boss`
 * is the default for any boss not listed. Applied to the HQ model in `EnemyViews.acquire`.
 */
export const ARCH_SCALE: Readonly<Record<string, number>> = {
  runner: 0.78, healer: 0.9, grunt: 0.92, bomber: 0.95, flyer: 1.0, shieldbearer: 1.08, brute: 1.3, boss: 1.95, boss_colossus: 2.8,
};
/**
 * Hand-measured muzzle points per tower id and tier (art/hq/README.md; model at the origin, front −z, so z here is
 * "forward"). The 2026-09-08 rest pose, stale since the towers were rebuilt: the live muzzle is measured from the
 * loaded model (views/towerMuzzle.ts), and this table only stands in when a model has no measurable part.
 */
const MUZZLE_FALLBACK: Record<string, [number, number][]> = {
  archer: [[1.27, 0.46], [1.54, 0.58], [1.81, 0.70]], cannon: [[1.12, 0.35], [1.30, 0.39], [1.48, 0.44]], frost: [[1.50, 0], [1.74, 0], [1.98, 0]],
  fire: [[0.70, 0.55], [0.82, 0.55], [0.94, 0.55]], lightning: [[2.62, 0], [3.00, 0], [3.38, 0]], ballista: [[1.03, 0.50], [1.23, 0.50], [1.42, 0.50]],
  poison: [[1.21, 0.57], [1.43, 0.57], [1.66, 0.57]], beacon: [[1.50, 0], [1.78, 0], [2.06, 0]], base: [[1.4, 0.3], [1.5, 0.3], [1.6, 0.3]],
};
/**
 * The fallback muzzle of a tower aimed at `yaw` (0 = +z), in world space: the stale table above when the HQ set is
 * loaded, else a rough guess. Callers prefer the measured muzzle (views/towerMuzzle.ts muzzleWorld).
 */
export function muzzleFallback(archId: string, tier: number, cell: Cell, yaw: number): THREE.Vector3 {
  const L = lib();
  const row = MUZZLE_FALLBACK[archId];
  if (!L || !row || !L.hq(hqTowerName(archId, archId === "base", tier))) return new THREE.Vector3(cell.x, SURFACE_Y + 0.9 + 0.35 * (tier - 1), cell.y);
  const [y, fwd] = row[Math.max(0, Math.min(2, tier - 1))];
  return new THREE.Vector3(cell.x + Math.sin(yaw) * fwd, y, cell.y + Math.cos(yaw) * fwd);
}
export const HQ_TROOP = "troop_soldier";
export const HQ_RALLY_FLAG = "rally_flag";
export const HQ_PORTAL = { base: "spawn_portal", ring: "spawn_portal_ring" };
/** Ground and road: grass variants by cell hash, path autotile pieces (end opens n, straight n–s, corner n–e). */
export const HQ_GRASS = ["g_grass0", "g_grass1", "g_grass2", "g_grass3", "g_grass4", "g_grass5"];
/** Blocker pool when anchors.json carries no `blocks` flags (the pre-v4 set). */
export const HQ_ROCKS = ["p_rocks", "p_crystals"];
export const HQ_PATH = { end: "g_path_end", straight: "g_path_straight", corner: "g_path_corner" };
/** Special road tiles (TileKind 1 mud, 2 bridge, 3 toll, 4 fire trap): dedicated straights, mud also has a corner. */
export const HQ_SPECIAL: Record<number, { straight: string; corner: string | null } | undefined> = {
  1: { straight: "g_path_mud_straight", corner: "g_path_mud_corner" }, 2: { straight: "g_bridge", corner: null },
  3: { straight: "g_path_toll_straight", corner: null }, 4: { straight: "g_path_fire_straight", corner: null },
};
/** Island edge: cliff tiles by which sides are exposed (canonical s / s+w / e+s+w / all four), water beyond. */
export const HQ_EDGE = { one: "g_grass0_cliff_s", strip: "g_grass0_cliff_ns", corner: "g_grass0_cliff_sw", peninsula: "g_grass0_cliff_esw", island: "g_grass0_cliff_nesw", water: "g_water", shore: "g_shore_s" };
/**
 * Cliff piece for a cell on the grid edge: which sides face the outside picks the model (canonical exposure s /
 * s+w / e+s+w / all four) and the extra yaw on top of HQ_YAW; interior cells return null.
 */
export function edgeTile(grid: Grid, c: Cell, extra: { n?: boolean; e?: boolean; s?: boolean; w?: boolean } = {}): { name: string; yaw: number } | null {
  const n = c.y === grid.height - 1 || !!extra.n, e = c.x === grid.width - 1 || !!extra.e, s = c.y === 0 || !!extra.s, w = c.x === 0 || !!extra.w;
  const count = +n + +e + +s + +w;
  if (count === 0) return null;
  if (count === 4) return { name: HQ_EDGE.island, yaw: 0 };
  if (count === 1) { const exposed = n ? Heading.North : e ? Heading.East : s ? Heading.South : Heading.West; return { name: HQ_EDGE.one, yaw: yawFor[((exposed + 2) & 3) as Heading] }; }
  if (count === 3) { const missing = !n ? Heading.North : !e ? Heading.East : !s ? Heading.South : Heading.West; return { name: HQ_EDGE.peninsula, yaw: yawFor[missing] }; }
  // Two sides: adjacent pairs are outer corners; opposite pairs (a one-cell-wide strip) fall back to the single cliff.
  if (s && w) return { name: HQ_EDGE.corner, yaw: 0 };
  if (w && n) return { name: HQ_EDGE.corner, yaw: -Math.PI / 2 };
  if (n && e) return { name: HQ_EDGE.corner, yaw: Math.PI };
  if (e && s) return { name: HQ_EDGE.corner, yaw: Math.PI / 2 };
  const strip = lib()?.hq(HQ_EDGE.strip) ? HQ_EDGE.strip : HQ_EDGE.one;
  return { name: strip, yaw: s || n ? 0 : Math.PI / 2 };
}
/** Ground-top height of the HQ set (top at 0) versus the no-HQ fallback (SURFACE_Y): things standing on the map use this. */
// `ships`, not `hq`: these ask "is the HQ kit in this build", and the grass tile is only loaded when the ground mesh is missing
export function surfaceY(): number { const L = lib(); return L && L.ships("g_grass0") ? 0 : SURFACE_Y; }
export function roadY(): number { const L = lib(); return L && L.ships("g_path_straight") ? 0 : ROAD_Y; }
/** Flyers travel this far above the road. */
export const FLYER_HEIGHT = 0.9;
/** Special road tiles without a dedicated HQ piece are the plain path piece, tinted (DESIGN §4.7): mud brown, bridge pale plank, toll gold. */
export const TILE_TINT: Record<number, number | undefined> = { 0: undefined, 1: 0x8a6a48, 2: 0xd8c8a8, 3: 0xffd76a, 4: 0xff8a5a };
/** A flat glyph on top of a special tile so the kind reads at a glance even without colour. */
export function tileMarker(kind: number): THREE.Object3D | null {
  if (kind === 0) return null;
  const g = new THREE.Group();
  if (kind === 1) { for (let i = 0; i < 3; i++) g.add(cube(new THREE.Vector3(-0.25 + i * 0.25, 0, (i % 2) * 0.2 - 0.1), 0.16, 0.03, 0.12, 0x5a4230)); }
  else if (kind === 2) { for (let i = 0; i < 4; i++) g.add(cube(new THREE.Vector3(0, 0, -0.3 + i * 0.2), 0.8, 0.03, 0.12, 0xb89c74)); }
  else if (kind === 3) { g.add(cube(new THREE.Vector3(-0.4, 0.25, 0), 0.08, 0.5, 0.08, 0xe0b040)); g.add(cube(new THREE.Vector3(0.4, 0.25, 0), 0.08, 0.5, 0.08, 0xe0b040)); g.add(cube(new THREE.Vector3(0, 0.5, 0), 0.9, 0.06, 0.08, 0xe0b040)); }
  else { g.add(cube(new THREE.Vector3(0, 0, 0), 0.4, 0.03, 0.4, 0xff6a3a)); }
  return g;
}

/**
 * A named part of an HQ model. The GLBs name nodes `<model>.<part>`, but THREE's GLTFLoader sanitises node names and
 * drops the dot (`base_t1.condgem0` arrives as `base_t1condgem0`), so look for both spellings.
 */
export function hqNode(root: THREE.Object3D, model: string, part: string): THREE.Object3D | null {
  return root.getObjectByName(`${model}${part}`) ?? root.getObjectByName(`${model}.${part}`) ?? null;
}
